import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, ThumbsUp, Check, MessageCircleQuestion, LogOut } from "lucide-react";
import AuthedImage from "@/components/AuthedImage";

const CURRENCY = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PortalProjectPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { client, logout } = usePortal();
  const [project, setProject] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (client === null) nav("/portal/login");
  }, [client, nav]);
  useEffect(() => {
    if (!client) return;
    portalApi.get(`/portal/projects/${id}`).then((r) => setProject(r.data)).catch(() => nav("/portal"));
  }, [id, client, nav]);

  if (!project) return <div className="min-h-screen p-12 text-muted-foreground">Loading…</div>;
  const p = project.current_proposal_version;

  const downloadPdf = async () => {
    try {
      const res = await portalApi.get(`/portal/projects/${id}/proposal/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `proposal-v${p?.version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const accept = async () => {
    if (!typedName.trim()) return toast.error("Please type your full name");
    setBusy(true);
    try {
      await portalApi.post(`/portal/proposal-versions/${p.id}/accept`, { typed_name: typedName });
      toast.success("Proposal accepted — thank you.");
      setAcceptOpen(false);
      const fresh = await portalApi.get(`/portal/projects/${id}`);
      setProject(fresh.data);
    } catch (e) { toast.error(formatErr(e) || "Could not accept"); }
    finally { setBusy(false); }
  };

  const feedback = async (product_id, action) => {
    try {
      await portalApi.post(`/portal/projects/${id}/feedback`, { product_id, action });
      toast.success("Thanks — noted");
      const fresh = await portalApi.get(`/portal/projects/${id}`);
      setProject(fresh.data);
    } catch { toast.error("Could not save"); }
  };

  const fbMap = {}; (project.material_feedback || []).forEach((f) => { fbMap[f.product_id] = f.action; });

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))]" data-testid="portal-project">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 flex justify-between items-center">
          <Link to="/portal" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" strokeWidth={1.5}/> All projects
          </Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-xs text-muted-foreground inline-flex gap-1"><LogOut className="w-3 h-3" strokeWidth={1.5}/> Sign out</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="overline">{project.project_number}</div>
        <h1 className="font-serif font-light mt-3" style={{ fontSize: "clamp(40px, 5vw, 56px)", lineHeight: 1.05 }}>{project.title}</h1>
        <div className="text-sm text-muted-foreground mt-3">Current stage — {project.delivery_stage || project.status}</div>

        <div className="grid md:grid-cols-3 gap-px bg-border border border-border mt-10">
          <Stat k="Room" v={project.room_type || "—"}/>
          <Stat k="Wall size" v={`${project.wall_width_mm}mm × ${project.wall_height_mm}mm`}/>
          <Stat k="Materials" v={`${(project.materials || []).length} items`}/>
        </div>

        {/* Design */}
        <section className="mt-14">
          <div className="overline">Design</div>
          <h2 className="font-serif font-light mt-2" style={{ fontSize: "32px" }}>Your Concept</h2>
          {project.design_version ? (
            <div className="mt-4 luxe-card p-6" data-testid="portal-current-concept">
              <div className="overline text-[10px]">Version {project.design_version.version} · Released {new Date(project.design_version.released_at).toLocaleDateString("en-AU")}</div>
              <div className="font-serif text-2xl mt-1">{project.design_version.title || `Concept V${project.design_version.version}`}</div>
              {project.design_version.client_description && (
                <p className="text-sm text-muted-foreground mt-3 max-w-2xl line-clamp-3">{project.design_version.client_description}</p>
              )}
              <Link to={`/portal/projects/${id}/concepts/${project.design_version.id}`} data-testid="btn-portal-view-concept" className="btn-primary mt-4 inline-flex">
                View concept & leave feedback →
              </Link>
              <Link to={`/portal/projects/${id}/concepts`} data-testid="btn-portal-view-all-concepts" className="btn-outline mt-4 ml-2 inline-flex">
                All concepts
              </Link>
            </div>
          ) : (
            <div className="mt-4 text-sm text-muted-foreground">Your design will appear here once the studio has released a concept version.</div>
          )}
          {project.images?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {project.images.map((im) => (
                <div key={im.id} className="aspect-square bg-muted overflow-hidden">
                  <AuthedImage path={im.storage_path} className="w-full h-full object-cover"/>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Materials */}
        <section className="mt-14">
          <div className="overline">Materials</div>
          <h2 className="font-serif font-light mt-2" style={{ fontSize: "32px" }}>Selected Materials</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            {project.materials?.map((m) => (
              <div key={m.id} className="luxe-card p-5 flex gap-4" data-testid={`portal-material-${m.id}`}>
                <div className="w-20 h-20 bg-muted overflow-hidden shrink-0">
                  <AuthedImage path={m.image_path} className="w-full h-full object-cover"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline text-[10px]">{m.category}</div>
                  <div className="font-serif text-lg">{m.name}</div>
                  <div className="text-xs text-muted-foreground">{m.quantity} {m.unit}</div>
                  {m.product_id && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <FbBtn active={fbMap[m.product_id] === "approved"} onClick={() => feedback(m.product_id, "approved")} icon={Check} label="Approve" testid={`fb-approve-${m.id}`}/>
                      <FbBtn active={fbMap[m.product_id] === "liked"} onClick={() => feedback(m.product_id, "liked")} icon={ThumbsUp} label="Like" testid={`fb-like-${m.id}`}/>
                      <FbBtn active={fbMap[m.product_id] === "alternative_requested"} onClick={() => feedback(m.product_id, "alternative_requested")} icon={MessageCircleQuestion} label="Alternative" testid={`fb-alt-${m.id}`}/>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!project.materials || project.materials.length === 0) && <div className="text-sm text-muted-foreground">Your material palette will appear here once the studio has released it.</div>}
          </div>
        </section>

        {/* Proposal */}
        <section className="mt-14">
          <div className="overline">Investment</div>
          <h2 className="font-serif font-light mt-2" style={{ fontSize: "32px" }}>Current Proposal</h2>
          {!p ? (
            <div className="mt-4 luxe-card p-6 text-sm text-muted-foreground">The studio hasn't issued a proposal yet. You'll receive an update when it's ready to view.</div>
          ) : (
            <div className="mt-4 luxe-card p-6">
              <div className="flex justify-between items-end flex-wrap gap-4">
                <div>
                  <div className="overline text-[10px]">{p.proposal_number} · Version {p.version}</div>
                  <div className="font-serif text-3xl mt-2">Total {CURRENCY(p.totals?.total)}</div>
                  <div className="text-xs text-muted-foreground mt-1">Inc. GST · AUD</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="btn-portal-download" onClick={downloadPdf} className="btn-outline"><Download className="w-4 h-4" strokeWidth={1.5}/> Download PDF</button>
                  {p.status !== "accepted" && (
                    <button data-testid="btn-portal-accept-open" onClick={() => setAcceptOpen(true)} className="btn-accent">Accept proposal</button>
                  )}
                  {p.status === "accepted" && (
                    <span className="btn-outline" style={{ background: "#3A4D39", color: "white", borderColor: "#3A4D39" }}>Accepted</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {acceptOpen && p && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="accept-modal">
          <div className="bg-card max-w-lg w-full p-8 border border-border">
            <div className="overline">Accept Proposal</div>
            <div className="font-serif text-3xl mt-2">Confirm your acceptance</div>
            <p className="text-sm text-muted-foreground mt-3">By typing your full name below and pressing Accept, you confirm your acceptance of proposal <strong>{p.proposal_number} V{p.version}</strong> for a total of <strong>{CURRENCY(p.totals?.total)}</strong> (inc. GST).</p>
            <input data-testid="input-accept-name" placeholder="Type your full name" className="luxe-input mt-6" value={typedName} onChange={(e) => setTypedName(e.target.value)}/>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setAcceptOpen(false)} className="btn-outline">Cancel</button>
              <button data-testid="btn-portal-accept" onClick={accept} disabled={busy} className="btn-accent">
                {busy ? "Accepting…" : "Accept"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="bg-card p-6">
      <div className="overline text-[10px]">{k}</div>
      <div className="font-serif text-xl mt-2">{v}</div>
    </div>
  );
}
function FbBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`text-xs flex items-center gap-1 px-3 py-1.5 border ${active ? "bg-foreground text-background border-foreground" : "border-input hover:border-foreground"}`}>
      <Icon className="w-3 h-3" strokeWidth={1.5}/> {label}
    </button>
  );
}
